import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';

import {
  fetchDraft,
  fetchDraftActions,
  fetchDraftChecks,
  fetchPermissions,
  fetchReleases,
} from '../services/taxonomy-read.service';

/** How often the open draft is re-read so other people's edits show up. */
const DRAFT_POLL_MS = 20_000;

/**
 * The open draft (null when none), polled every 20 s and on window focus. The first
 * sighting of a draft records its version as the "known" baseline, so later jumps that
 * are not our own writes read as changes by others.
 */
export function useDraft() {
  return useQuery({
    queryKey: queryKeys.taxonomyStudio.draft(),
    queryFn: async () => {
      const draft = await fetchDraft();
      const store = useTaxonomyStudioStore.getState();
      if (draft?.draftVersion == null) {
        store.reset();
      } else if (store.knownVersion === null || store.draftSeq !== draft.seq) {
        store.setKnown(draft.seq, draft.draftVersion);
      }
      return draft;
    },
    refetchInterval: DRAFT_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

/** Publish checks — only meaningful while a draft is open. */
export function useDraftChecks(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.taxonomyStudio.checks(),
    queryFn: fetchDraftChecks,
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/** The caller's undoable actions in the draft, newest first. */
export function useDraftActions(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.taxonomyStudio.actions(),
    queryFn: fetchDraftActions,
    enabled,
    staleTime: 5_000,
  });
}

/** Every release, newest first (drives the History picker and the Versions page). */
export function useReleases() {
  return useQuery({
    queryKey: queryKeys.taxonomyStudio.releases(),
    queryFn: fetchReleases,
    staleTime: 30_000,
  });
}

/** What the signed-in user may do (`can_write`, `can_publish`). */
export function useTaxonomyPermissions() {
  return useQuery({
    queryKey: queryKeys.taxonomyStudio.permissions(),
    queryFn: fetchPermissions,
    staleTime: 5 * 60_000,
  });
}
